import { EthereumChainId } from '@injectivelabs/ts-types'
import { NetworkEndpoints } from '@injectivelabs/networks'
import { TransactionException, UnspecifiedErrorCode } from '@injectivelabs/exceptions'
import { BigNumberInBase, getStdFee } from '@injectivelabs/utils'
import {
  ChainRestAuthApi,
  ChainRestTendermintApi,
  getGasPriceBasedOnMessage,
  getEip712TypedDataV2,
  createTransaction,
  SIGN_EIP712_V2,
  createWeb3Extension,
  createTxRawEIP712,
  hexToBuff,
  Msgs,
  getInjectiveAddress,
  TxRestApi,
} from '@ninjablaze/sdk-ts'
import { recoverPublicKey, hashTypedData } from 'viem'
import { FEE_PAYER_INJ } from '@/constants/constants'

const getEthereumWalletPubKey = async ({ eip712TypedData, signature }: { eip712TypedData: any; signature: string }) => {
  const message = hashTypedData(eip712TypedData)
  return Buffer.from(
    (
      await recoverPublicKey({
        hash: message,
        signature: Buffer.from(signature.replace('0x', ''), 'hex'),
      })
    ).replace('0x', ''),
    'hex'
  ).toString('base64')
}
export interface AccountType {
  account?: { address: string; type: string }
  address: string
  signTypedData(data: ReturnType<typeof getEip712TypedDataV2>): Promise<string>
}

export class WalletBroadcaster {
  constructor(
    private account: AccountType,
    private options: {
      chainId: string
      ethereumChainId: EthereumChainId
      endpoints: NetworkEndpoints
    }
  ) {}

  async sendTransaction(tx: { msgs: Msgs[]; memo?: string; gas?: { gas: number } }) {
    const { chainId, ethereumChainId, endpoints } = this.options

    const msgs = Array.isArray(tx.msgs) ? tx.msgs : [tx.msgs]

    const injectiveAddress = getInjectiveAddress(this.account.address)

    /** Account Details * */
    const accountDetails = await new ChainRestAuthApi(endpoints.rest).fetchAccount(injectiveAddress)
    const { base_account: baseAccount } = accountDetails.account

    /** Block Details */
    const latestBlock = await new ChainRestTendermintApi(endpoints.rest).fetchLatestBlock()
    const latestHeight = latestBlock!.header!.height
    const timeoutHeight = new BigNumberInBase(latestHeight).plus(100)

    const gas = (tx.gas?.gas || getGasPriceBasedOnMessage(msgs)).toString()
    let stdFee = getStdFee({ ...tx.gas, gas })

    stdFee.feePayer = FEE_PAYER_INJ

    /** EIP712 for signing on Ethereum wallets */
    const eip712TypedData = getEip712TypedDataV2({
      msgs,
      fee: stdFee,
      tx: {
        memo: tx.memo,
        accountNumber: baseAccount.account_number.toString(),
        sequence: baseAccount.sequence.toString(),
        timeoutHeight: timeoutHeight.toFixed(),
        chainId,
      },
      ethereumChainId,
    })

    /** Signing on Ethereum */
    const signature = await this.account.signTypedData(eip712TypedData as any)
    const pubKeyOrSignatureDerivedPubKey = await getEthereumWalletPubKey({
      eip712TypedData,
      signature,
    })

    const { txRaw } = createTransaction({
      message: msgs,
      memo: tx.memo,
      signMode: SIGN_EIP712_V2,
      fee: stdFee,
      pubKey: pubKeyOrSignatureDerivedPubKey,
      sequence: Number(baseAccount.sequence),
      timeoutHeight: timeoutHeight.toNumber(),
      accountNumber: Number(baseAccount.account_number),
      chainId,
    })

    const web3Extension = createWeb3Extension({
      ethereumChainId,
    })

    const signResponse = await fetch(endpoints.web3gw! + '/sign', {
      method: 'POST',
      body: JSON.stringify(eip712TypedData),
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
      },
    }).then((res) => res.json())

    web3Extension.feePayer = FEE_PAYER_INJ
    web3Extension.feePayerSig = Buffer.from(signResponse.signature, 'hex')

    const txRawEip712 = createTxRawEIP712(txRaw, web3Extension)

    /** Append Signatures */
    txRawEip712.signatures = [hexToBuff(signature)]

    const txApi = new TxRestApi(endpoints.rest)
    const response = await txApi.broadcast(txRawEip712, { txTimeout: 5000 })

    if (response.code !== 0) {
      throw new TransactionException(new Error(response.rawLog), {
        code: UnspecifiedErrorCode,
        contextCode: response.code,
        contextModule: response.codespace,
      })
    }

    return response
  }
}
